import { areUserRolesIn } from '../../../../../helpers';
import { ClinicalRelationships, Roles } from '../../../../../constants';
import { ClinicalStatus } from '../../../../../admin/constants/general';

import type {
  IBtSupervision,
  IClient,
  IOption,
  IProviderAssignment,
} from '../../../../../utils/types';

export interface BtSupervisionClientOptionsParams {
  btSupervisionData: IBtSupervision;
  isClientNameSelectDisabled: boolean;
  userRoles: Roles[] | undefined;
  providerAssignments: IProviderAssignment[] | undefined;
  clients: IClient[] | undefined;
}

function optionsFromEmbeddedApiClient(client: IClient): IOption[] {
  if (client.id == null) {
    return [];
  }

  return [
    {
      id: client.id,
      label: `${client.first_name} ${client.last_name}`,
      description: '',
    },
  ];
}

function descriptionForAssignment(
  clinicalRelationship: IProviderAssignment['clinical_relationship'],
): string {
  if (!clinicalRelationship || clinicalRelationship === ClinicalRelationships.HomeBcba) {
    return '';
  }
  return clinicalRelationship.split('_')[0] ?? '';
}

function optionsFromBcbaProviderAssignments(
  providerAssignments: IProviderAssignment[] | undefined,
): IOption[] {
  const clinicalClients =
    providerAssignments?.filter((pa) => pa.client?.clinical_status === ClinicalStatus.CLINICAL) ??
    [];

  const mapped = clinicalClients.flatMap((assignment) =>
    assignment.client.id == null
      ? []
      : [
          {
            id: assignment.client.id,
            label: `${assignment.client.first_name} ${assignment.client.last_name}`,
            description: descriptionForAssignment(assignment.clinical_relationship),
          },
        ],
  );

  return mapped.sort((a, b) => a.label.localeCompare(b.label));
}

function optionsFromClientList(clients: IClient[] | undefined): IOption[] {
  const mapped =
    clients?.flatMap((client) =>
      client.id == null
        ? []
        : [
            {
              id: client.id,
              label: `${client.first_name} ${client.last_name}`,
            },
          ],
    ) ?? [];

  return [...mapped].sort((a, b) => a.label.localeCompare(b.label));
}

export function getBtSupervisionClientAutocompleteOptions(
  params: BtSupervisionClientOptionsParams,
): IOption[] {
  const { btSupervisionData, isClientNameSelectDisabled, userRoles, providerAssignments, clients } =
    params;

  const useSingleEmbeddedClientOption =
    Boolean(btSupervisionData.client) && isClientNameSelectDisabled;

  if (useSingleEmbeddedClientOption && btSupervisionData.client) {
    return optionsFromEmbeddedApiClient(btSupervisionData.client);
  }

  const userIsBcba = areUserRolesIn(userRoles ?? [], [Roles.Bcba]);

  if (userIsBcba) {
    return optionsFromBcbaProviderAssignments(providerAssignments);
  }

  return optionsFromClientList(clients);
}

export function getSelectedClientDetailsForBtSupervision(
  clientId: number | null,
  clients: IClient[],
): IClient | null {
  if (!clientId || clients.length === 0) {
    return null;
  }
  return clients.find((c) => c.id === clientId) ?? null;
}

export function resolveClientIdForBtSupervisionCohortAssignments(
  formClientId: number | null,
  supervisionClientId: number | null | undefined,
): number | null {
  if (formClientId != null) {
    return formClientId;
  }
  if (supervisionClientId != null) {
    return supervisionClientId;
  }
  return null;
}
